/**
 * Surfaces outstanding workspace invitations while the user browses the app (session link or server-side pending list).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, X } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import {
  getPendingInviteToken,
  INVITE_BANNER_DISMISS_STORAGE_KEY,
} from '@/services/workspaceApi';

const DISMISS_KEY = INVITE_BANNER_DISMISS_STORAGE_KEY;

export default function PendingInviteBanner() {
  const { user, pendingInvites } = useAuth();
  const { pathname } = useLocation();
  const [dismissedFor, setDismissedFor] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) || '';
    } catch {
      return '';
    }
  });

  const sessionToken = typeof window !== 'undefined' ? getPendingInviteToken().trim() : '';
  const list = pendingInvites || [];

  const { primaryToken, label, showMultiHint } = useMemo(() => {
    if (sessionToken) {
      return {
        primaryToken: sessionToken,
        label:
          'You opened an invitation link — sign-in is done; finish here to join that workspace.',
        showMultiHint: list.length > 0,
      };
    }
    if (list.length === 0) {
      return { primaryToken: '', label: '', showMultiHint: false };
    }
    const tok = (list[0].token || '').trim();
    if (list.length === 1) {
      return {
        primaryToken: tok,
        label: `You're invited to ${list[0].name || 'a workspace'}. Accept to join.`,
        showMultiHint: false,
      };
    }
    return {
      primaryToken: tok,
      label: `You have ${list.length} pending workspace invitations.`,
      showMultiHint: true,
    };
  }, [sessionToken, list]);

  const inviteKey =
    sessionToken || (list.length ? [...list].map((p) => p.token).sort().join('|') : '');

  useEffect(() => {
    try {
      const prev = sessionStorage.getItem(DISMISS_KEY) || '';
      if (inviteKey && prev && prev !== inviteKey) {
        sessionStorage.removeItem(DISMISS_KEY);
        setDismissedFor('');
      }
    } catch {
      /* ignore */
    }
  }, [inviteKey]);

  /** No pending invites and no session invite link — reset dismiss state so a future invite isn't hidden. */
  useEffect(() => {
    if (list.length > 0 || sessionToken) return;
    try {
      sessionStorage.removeItem(DISMISS_KEY);
    } catch {
      /* ignore */
    }
    setDismissedFor('');
  }, [list.length, sessionToken]);

  const onDismiss = () => {
    if (!inviteKey) return;
    try {
      sessionStorage.setItem(DISMISS_KEY, inviteKey);
    } catch {
      /* ignore */
    }
    setDismissedFor(inviteKey);
  };

  if (!user) return null;
  if (pathname === '/join') return null;
  if (!primaryToken && list.length === 0) return null;
  if (inviteKey && dismissedFor === inviteKey) return null;

  const to = `/join?token=${encodeURIComponent(primaryToken)}`;

  return (
    <div
      className="flex shrink-0 items-start gap-3 border-b border-amber-200/90 bg-gradient-to-r from-amber-50 to-amber-50/50 px-4 py-3 sm:items-center sm:px-5"
      role="region"
      aria-label="Pending workspace invitation"
    >
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
        <Mail className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-amber-950">{label}</p>
        {showMultiHint ? (
          <p className="mt-0.5 text-xs text-amber-800/90">
            Use the workspace menu to open other pending invites (marked Pending).
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Link
            to={to}
            className="inline-flex items-center rounded-lg bg-amber-800 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-amber-900"
          >
            {sessionToken ? 'Continue to join' : 'Review invitation'}
          </Link>
          <button
            type="button"
            onClick={onDismiss}
            className="inline-flex items-center gap-1 rounded-lg border border-amber-300/80 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100/80"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Hide for now
          </button>
        </div>
      </div>
    </div>
  );
}
